/**
 * Display and field enabling/disabling rules by plan type
 * 
 * Structure facilitates adding/removing specific rules for each plan
 */

import { ALL_PLANOS } from '../../../utils/constantes';

/**
 * Returns the installment quantity limits by plan type
 * @param {string} planoNome - Selected plan name (ex: 'À Vista')
 * @param {number} quantidadeParcelasPSA - PSA installment quantity returned by API (for Construction Period)
 * @param {boolean} eSAD - Flag indicating if SAD (Dynamic Amortization System)
 * @param {number} quantidadeMaximaParcelasMensais - Maximum monthly installments returned by initialization API
 * @returns {Object} - Object with min and max installments { min: number, max: number }
 */
export const getParcelasLimits = (planoNome, quantidadeParcelasPSA = null, eSAD = false, quantidadeMaximaParcelasMensais = null) => {
  const limits = {
    [ALL_PLANOS.AVista]: { min: 0 },         
    [ALL_PLANOS.Fixo12]: { min: 3 },         
    [ALL_PLANOS.Fixo18]: { min: 3 },         
    [ALL_PLANOS.PrazoObra]: { min: 3, },     
    [ALL_PLANOS.PosObra]: { min: 3},         
    [ALL_PLANOS.FinanciamentoBancario]: { min: 3}  
  };

  // For Post-Construction, max varies according to SAD
  if (planoNome === ALL_PLANOS.PosObra) {
    return {
      ...limits[planoNome],
      max: quantidadeMaximaParcelasMensais || 84  // Use initialization API value, fallback to 84
    };
  }

  return limits[planoNome] || { min: 0, max: 36 };  // Default: 0 to 36
};

/**
 * Defines field rules (disabled and visible) for each plan type
 * @param {string} planoNome - Selected plan name (ex: 'À Vista')
 * @returns {Object} - Object with disabled and visible rules for each field
 */
export const getPlanoFieldRules = (planoNome) => {
  // When no plan is selected, all fields are disabled but visible
  if (!planoNome) {
    return {
      tipoFluxo: { disabled: true, visible: true },
      tipoCorrecao: { disabled: true, visible: true },
      quantidadeParcelas: { disabled: true, visible: true },
      ato: { disabled: true, visible: true },
      e2: { disabled: true, visible: true },
      e3: { disabled: true, visible: true },
      aumento: { disabled: true, visible: true },
      desconto: { disabled: true, visible: true },
      fgts: { disabled: true, visible: true },
      renda: { disabled: true, visible: false },
      carteira: { disabled: true, visible: false },
      eSAD: { disabled: true, visible: false },
      padraoPsa: { disabled: true, visible: false },
      intermediarias: { visible: false }
    };
  }

 // Plan-specific rules (using ALL_PLANOS enum keys)
  const rules = {
    [ALL_PLANOS.AVista]: {
      tipoFluxo: { disabled: true, visible: true },
      tipoCorrecao: { disabled: true, visible: true },
      quantidadeParcelas: { disabled: true, visible: true },
      ato: { disabled: false, visible: true },
      e2: { disabled: false, visible: true },
      e3: { disabled: false, visible: true },
      aumento: { disabled: false, visible: true },
      desconto: { disabled: false, visible: true },
      fgts: { disabled: false, visible: true },
      renda: { disabled: false, visible: false },
      carteira: { disabled: false, visible: false },
      eSAD: { disabled: false, visible: false },
      padraoPsa: { disabled: false, visible: false },
      intermediarias: { visible: false }
    },
    [ALL_PLANOS.Fixo12]: {
      tipoFluxo: { disabled: true, visible: true },
      tipoCorrecao: { disabled: true, visible: true },
      quantidadeParcelas: { disabled: false, visible: true, min: 1, max: 12 },
      ato: { disabled: false, visible: true },
      e2: { disabled: true, visible: true },
      e3: { disabled: true, visible: true },
      aumento: { disabled: false, visible: true },
      desconto: { disabled: false, visible: true },
      fgts: { disabled: false, visible: true },
      renda: { disabled: false, visible: false },
      carteira: { disabled: false, visible: false },
      eSAD: { disabled: false, visible: false },
      padraoPsa: { disabled: false, visible: false },
      intermediarias: { visible: false }
    },
    [ALL_PLANOS.Fixo18]: {
      tipoFluxo: { disabled: true, visible: true },
      tipoCorrecao: { disabled: true, visible: true },
      quantidadeParcelas: { disabled: false, visible: true, min: 1, max: 18 },
      ato: { disabled: false, visible: true },
      e2: { disabled: true, visible: true },
      e3: { disabled: true, visible: true },
      aumento: { disabled: false, visible: true },
      desconto: { disabled: false, visible: true },
      fgts: { disabled: false, visible: true },
      renda: { disabled: false, visible: false },
      carteira: { disabled: false, visible: false },
      eSAD: { disabled: false, visible: false },
      padraoPsa: { disabled: false, visible: false },
      intermediarias: { visible: false }
    },
    [ALL_PLANOS.PrazoObra]: {
      tipoFluxo: { disabled: true, visible: true },
      tipoCorrecao: { disabled: true, visible: true },
      quantidadeParcelas: { disabled: false, visible: true },
      ato: { disabled: false, visible: true },
      e2: { disabled: false, visible: true },
      e3: { disabled: false, visible: true },
      aumento: { disabled: false, visible: true },
      desconto: { disabled: false, visible: true },
      fgts: { disabled: false, visible: true },
      renda: { disabled: false, visible: true },
      carteira: { disabled: false, visible: false },
      eSAD: { disabled: false, visible: false },
      padraoPsa: { disabled: false, visible: false },
      intermediarias: { visible: true }
    },
    [ALL_PLANOS.PosObra]: {
      tipoFluxo: { disabled: false, visible: true },
      tipoCorrecao: { disabled: false, visible: true },
      quantidadeParcelas: { disabled: false, visible: true },
      ato: { disabled: false, visible: true },
      e2: { disabled: false, visible: true },
      e3: { disabled: false, visible: true },
      aumento: { disabled: false, visible: true },
      desconto: { disabled: false, visible: true },
      fgts: { disabled: false, visible: false },
      renda: { disabled: false, visible: true },
      carteira: { disabled: false, visible: false },
      eSAD: { disabled: false, visible: true },
      padraoPsa: { disabled: false, visible: true },
      intermediarias: { visible: true }
    },
    [ALL_PLANOS.FinanciamentoBancario]: {
      tipoFluxo: { disabled: false, visible: true },
      tipoCorrecao: { disabled: false, visible: true },
      quantidadeParcelas: { disabled: false, visible: true },
      ato: { disabled: false, visible: true },
      e2: { disabled: false, visible: true },
      e3: { disabled: false, visible: true },
      aumento: { disabled: false, visible: true },
      desconto: { disabled: false, visible: true },
      fgts: { disabled: false, visible: false },
      renda: { disabled: false, visible: true },
      carteira: { disabled: false, visible: true },
      eSAD: { disabled: false, visible: false },
      padraoPsa: { disabled: false, visible: false },
      intermediarias: { visible: true }
    },
  };

  // Return plan-specific rules or default rules (all enabled and visible)
  return rules[planoNome] || {
    tipoFluxo: { disabled: false, visible: true },
    tipoCorrecao: { disabled: false, visible: true },
    quantidadeParcelas: { disabled: false, visible: true },
    ato: { disabled: false, visible: true },
    e2: { disabled: false, visible: true },
    e3: { disabled: false, visible: true },
    aumento: { disabled: false, visible: true },
    desconto: { disabled: false, visible: true },
    fgts: { disabled: false, visible: true },
    renda: { disabled: false, visible: false },
    carteira: { disabled: false, visible: false },
    eSAD: { disabled: false, visible: false },
    padraoPsa: { disabled: false, visible: false },
    intermediarias: { visible: true }
  };
};
